import { reactive } from 'vue';

import { pageService, Page } from '../services/pages';
import { session } from '../globals/session';

/*
  The navigation endpoint (`app/pages/navigation`) is not a paginated CRUD
  resource, so it does not fit the generic BaseStore. This lightweight store
  wraps pageService.navigation() and shares a single reactive list of pages
  across every navigation bar (mobile tab bar + desktop menu), so the bars
  read from the store instead of calling the service directly.

  The last successful result is persisted to localStorage, namespaced by the
  access token like BaseStore (`store__<token-prefix>__pages_navigation`), so
  the nav bar renders from cache on an offline cold start. The key lives under
  the `store__` prefix, so accountService.logout() -> deleteUserCache() purges
  it (and, like the other stores, keeps it for the 'blue' brand).
*/
class PageStore {
  private state = reactive<{ pages: Page[] }>({ pages: [] });

  constructor() {
    this.loadFromCache();

    // Navigation is per-user (role/permissions). Drop the in-memory pages on
    // logout from the store itself, so a different user can never see the
    // previous user's nav regardless of which chrome (mobile tab bar or
    // desktop menu) is mounted. Loading stays UI-driven (the bars call
    // retrieveNavigation() when shown), so we only need to react to 'reset'.
    session.on('reset', () => this.clear());
  }

  // Mirrors BaseStore.getResourceName(): namespaced by the current access
  // token so each user reads their own cached navigation. Read fresh every
  // call (the singleton outlives login/logout, so the token changes under it).
  private storageKey(): string {
    let prefix = 'store__';
    if (localStorage.accessToken) prefix += localStorage.accessToken.slice(0, 4) + '__';
    return prefix + 'pages_navigation';
  }

  private loadFromCache(): void {
    // Only an authenticated user has navigation; skip the degenerate tokenless
    // key so a logged-out context can never read another user's cached pages.
    if (!localStorage.accessToken) return;
    try {
      const raw = localStorage.getItem(this.storageKey());
      // Assign only on a cache hit — never clobber good in-memory pages with []
      // when this user simply has nothing cached yet (e.g. offline, no cache).
      if (raw) this.state.pages = JSON.parse(raw) as Page[];
    } catch (e) {
      console.warn('[pageStore] Failed to load cached navigation', e);
    }
  }

  private persist(): void {
    // Never write the degenerate tokenless key (see loadFromCache).
    if (!localStorage.accessToken) return;
    try {
      localStorage.setItem(this.storageKey(), JSON.stringify(this.state.pages));
    } catch (e) {
      // localStorage may be full or unavailable; keep the in-memory pages.
      console.warn('[pageStore] Failed to persist navigation', e);
    }
  }

  list(): Page[] {
    return this.state.pages;
  }

  async retrieveNavigation(): Promise<Page[]> {
    const response = await pageService.navigation();
    if (response) {
      this.state.pages = response.data?.sidebar ?? [];
      this.persist();
    } else {
      // navigation() resolves to undefined when offline (see BaseService._get
      // / the axios Network Error interceptor); fall back to this user's cached
      // pages so the nav bar still renders (a cache miss leaves state intact).
      this.loadFromCache();
    }
    return this.state.pages;
  }

  clear(): void {
    // Memory only — the persisted cache is token-namespaced and is purged by
    // accountService.logout() -> deleteUserCache() (which keeps caches for the
    // 'blue' brand), so we mirror BaseStore.clearMemory() and leave it.
    this.state.pages = [];
  }
}

export const pageStore = new PageStore();
